import classNames from 'classnames';
import { sortBy } from 'lodash';
import React, { useState, MouseEvent } from 'react';
import { Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';

// Ducks
import { RootState } from '../../../ducks/configureStore';
import ProgressBar from '../../common/ProgressBar';

import './PrePostPerformanceReport.scss';

interface OpenRow {
  [key: string]: boolean;
}

const calculateScore = (correct: number | undefined, total: number | undefined) => {
  if (correct && total) {
    if (total || total === 0) {
      return Math.round((correct / total) * 100);
    }
  }
  return undefined;
};

const PrePostPeformanceReport = () => {
  // REDUX STATE
  const { categories, overall, preName, instructions } = useSelector((state: RootState) => {
    const { slidesLitmos } = state;
    const { currentSlide } = slidesLitmos;
    const { learnerData, instructions } = currentSlide || {};
    const { categories, overall, preName } = learnerData || {};
    return {
      categories: categories || {},
      overall: overall || {},
      preName,
      instructions,
    };
  });

  // LOCAL STATE
  const [openRows, setOpenRows] = useState({} as OpenRow);

  const onRowClick = (event: MouseEvent) => {
    const id = event.currentTarget.id;
    const isOpen = openRows[id];
    setOpenRows({
      ...openRows,
      [id]: !isOpen,
    });
  };

  const renderScore = (score: number | undefined, total: number | undefined) => {
    if (total === 1) {
      const icon = score ? 'done' : 'close';
      return (
        <div className="progress-score">
          <i
            className={classNames('material-icons', {
              correct: score,
              incorrect: !score,
            })}
          >
            {icon}
          </i>
        </div>
      );
    }

    return (
      <ProgressBar
        value={score}
        showOnRight
        // These values added to appease typescript
        label=""
        className=""
        showOnBar={false}
        small={false}
      />
    );
  };

  const getTableRows = () => {
    return sortBy(Object.keys(categories)).map((key) => {
      const rows = [];
      const category = categories[key];
      const { preCorrect, preTotal, postCorrect, postTotal, tags } = category;
      const preScore = calculateScore(preCorrect, preTotal);
      const postScore = calculateScore(postCorrect, postTotal);
      const isOpen = openRows[key];
      const hasTags = !!tags;

      rows.push(
        <tr key={key} id={key} onClick={hasTags ? onRowClick : undefined} className="parent-row">
          <td>
            <div className="row-label">
              {hasTags && (
                <i className="material-icons">{isOpen ? 'expand_more' : 'chevron_right'}</i>
              )}
              <span>{key}</span>
            </div>
          </td>
          <td className="pre">{renderScore(preScore, preTotal)}</td>
          <td className="post">{renderScore(postScore, postTotal)}</td>
        </tr>,
      );

      // TODO - This might be utilized in the future. Leaving commented code for
      // possible starting point.
      // if(isOpen){
      //   const children = sortBy(Object.keys(tags)).map((tag) => {
      //     const currentTag = tags[tag as any];
      //     const tagPreScore = calculateScore(currentTag.preCorrect, currentTag.preTotal);
      //     const tagPostScore = calculateScore(currentTag.postCorrect, currentTag.postTotal);

      //     return(
      //       <tr key={tag} className="child-row">
      //         <td>
      //           <div className="row-label child">{tag}</div>
      //         </td>
      //         <td className="pre">
      //           <ProgressBar
      //             value={tagPreScore}
      //             showOnRight
      //             small
      //             // These values added to appease typescript
      //             label=''
      //             className=''
      //             showOnBar={false}
      //           />
      //         </td>
      //         <td className="post">
      //           <ProgressBar
      //             value={tagPostScore}
      //             showOnRight
      //             small
      //             // These values added to appease typescript
      //             label=''
      //             className=''
      //             showOnBar={false}
      //           />
      //         </td>
      //       </tr>
      //     );
      //   });

      //   rows.push(...children);
      // }

      return rows;
    });
  };

  const renderReport = () => {
    const { preCorrect, preTotal, postCorrect, postTotal } = overall;
    const preScore = calculateScore(preCorrect, preTotal);
    const postScore = calculateScore(postCorrect, postTotal);
    const tableRows = getTableRows();
    return (
      <div className="prepost-report">
        <div className="report-header">
          <h1>Performance Report</h1>
          <div>
            {instructions ||
              `Compare your performance in this module and the ${preName || 'Pre-Test'} module`}
          </div>
        </div>
        <div className="report-content">
          <Table hover size="sm">
            <thead>
              <tr className="report-table-header">
                <th />
                <th className="pre">{preName || 'Pre-Test'}</th>
                <th className="post">This Module</th>
              </tr>
            </thead>
            <tbody>
              <tr className="overall-row">
                <td>
                  <div className="overall-header">Overall</div>
                </td>
                <td className="pre">
                  <ProgressBar
                    value={preScore}
                    showOnRight
                    // These values added to appease typescript
                    label=""
                    className=""
                    showOnBar={false}
                    small={false}
                  />
                </td>
                <td className="post">
                  <ProgressBar
                    value={postScore}
                    showOnRight
                    // These values added to appease typescript
                    label=""
                    className=""
                    showOnBar={false}
                    small={false}
                  />
                </td>
              </tr>
              {tableRows}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  return <div className="PrePostPerformanceReport">{renderReport()}</div>;
};

export default PrePostPeformanceReport;
